import React, { useState, useEffect } from 'react';
import {
  collection, query, where, getDocs, orderBy, limit,
  addDoc, deleteDoc, doc, getDoc, serverTimestamp, updateDoc
} from 'firebase/firestore';
import { db, getAllArtistsWithFollowerCounts, blockFollowerCounts, unblockFollowerCounts, backfillAllArtistProfiles } from '../firebase-utils';
import { BarChart3, Users, Music, Calendar, TrendingUp, Activity, Clock } from 'lucide-react';

const ADMIN_EMAIL = 'gig.wave.2005@gmail.com';

const GENRES = ['Rock', 'Pop', 'Blues', 'Jazz', 'Country', 'R&B', 'Soul', 'Funk',
  'Metal', 'Punk', 'Folk', 'Reggae', 'Hip-Hop', 'Electronic', 'Classical', 'Other'];

const extractYouTubeId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /^([a-zA-Z0-9_-]{11})$/,
  ];
  for (const p of patterns) { const m = url.match(p); if (m) return m[1]; }
  return null;
};

const AdminDashboard = ({ currentUser, visitorCount, onClose }) => {
  const [adminTab, setAdminTab] = useState('analytics');

  // ── Analytics state ──
  const [stats, setStats] = useState({
    totalArtists: 0, totalUsers: 0, totalGigs: 0, liveGigs: 0, completedGigs: 0,
    totalAudience: 0, totalVotes: 0, totalSongRequests: 0,
    recentSignups: [], recentGigs: [], loading: true
  });
  const [timeRange, setTimeRange] = useState('all');

  // ── Song Bank state ──
  const [tracks, setTracks] = useState([]);
  const [bankLoading, setBankLoading] = useState(false);
  const [bankSearch, setBankSearch] = useState('');
  const [previewId, setPreviewId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [form, setForm] = useState({ title: '', artist: '', genre: 'Rock', youtubeUrl: '' });
  const [pendingSongs, setPendingSongs] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);

  // ── Followers tab state ──
  const [followersArtists, setFollowersArtists] = useState([]);
  const [followersLoading, setFollowersLoading] = useState(false);
  const [followersSearch, setFollowersSearch] = useState('');
  const [blockingId, setBlockingId] = useState(null);

  // ── Backfill state ──
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  useEffect(() => {
    if (currentUser?.email === ADMIN_EMAIL) fetchAnalytics();
  }, [timeRange, currentUser]);

  useEffect(() => {
    if (currentUser?.email === ADMIN_EMAIL && adminTab === 'songbank') {
      loadTracks();
      loadPendingSongs();
    }
  }, [adminTab, currentUser]);

  useEffect(() => {
    if (currentUser?.email === ADMIN_EMAIL && adminTab === 'followers') {
      loadFollowersData();
    }
  }, [adminTab, currentUser]);

  // ════════════════════════════════════════
  // FOLLOWERS TAB
  // ════════════════════════════════════════
  const loadFollowersData = async () => {
    setFollowersLoading(true);
    try {
      const artists = await getAllArtistsWithFollowerCounts();
      setFollowersArtists(artists);
    } catch (e) { console.error(e); }
    setFollowersLoading(false);
  };

  const handleBackfillArtists = async () => {
    if (!window.confirm('This will sync ALL artist profiles to the search index. This may take a minute for large numbers of users. Continue?')) return;
    setBackfilling(true);
    setBackfillResult(null);
    try {
      const result = await backfillAllArtistProfiles();
      setBackfillResult(result);
      alert(`✅ Sync complete!\n${result.synced} artists synced\n${result.skipped} skipped (no artist profile)`);
    } catch (e) { alert('Error: ' + e.message); }
    finally { setBackfilling(false); }
  };

  const handleBlockFollowers = async (artistId, artistName) => {
    if (!window.confirm(`Block follower counts for ${artistName}? They will be notified to contact support.`)) return;
    setBlockingId(artistId);
    try {
      await blockFollowerCounts(artistId);
      setFollowersArtists(prev => prev.map(a =>
        a.id === artistId ? { ...a, followerCountsBlocked: true } : a
      ));
      alert(`✅ Follower counts hidden for ${artistName}. They will see a banner to contact support.`);
    } catch (e) { alert('Error: ' + e.message); }
    setBlockingId(null);
  };

  const handleUnblockFollowers = async (artistId, artistName) => {
    if (!window.confirm(`Reinstate follower counts for ${artistName}?`)) return;
    setBlockingId(artistId);
    try {
      await unblockFollowerCounts(artistId);
      setFollowersArtists(prev => prev.map(a =>
        a.id === artistId ? { ...a, followerCountsBlocked: false } : a
      ));
      alert(`✅ Follower counts reinstated for ${artistName}.`);
    } catch (e) { alert('Error: ' + e.message); }
    setBlockingId(null);
  };

  // ════════════════════════════════════════
  // ANALYTICS — fixed to query users/{id}/profile/data
  // ════════════════════════════════════════
  const fetchAnalytics = async () => {
    try {
      setStats(prev => ({ ...prev, loading: true }));

      // ── 1. Artist count — use artistProfiles (fast single query, no permission issues) ──
      const artistProfilesSnap = await getDocs(collection(db, 'artistProfiles'));
      const allArtists = artistProfilesSnap.docs.map(d => ({
        id: d.id,
        artistName: d.data().artistName || '',
        fullName: d.data().fullName || '',
        genre: d.data().genre || '',
        location: d.data().location || '',
        // artistProfiles doesn't store email or createdAt — get those from users collection
        createdAt: d.data().updatedAt || null, // best we have in artistProfiles
      }));

      // ── 2. Total registered users (all accounts, not just artists) ──
      const usersSnap = await getDocs(collection(db, 'users'));
      const totalUsers = usersSnap.size;

      // Build email lookup from users collection for recent signups display
      const userEmailMap = {};
      usersSnap.docs.forEach(u => {
        userEmailMap[u.id] = u.data().email || '';
      });

      // Enrich artists with email + proper createdAt from users doc
      const enrichedArtists = allArtists.map(a => ({
        ...a,
        email: userEmailMap[a.id] || '',
        createdAt: a.createdAt, // updatedAt from artistProfiles as proxy
      }));

      // Sort by updatedAt descending (most recently active first)
      const sortedArtists = [...enrichedArtists].sort((a, b) => {
        const tA = a.createdAt?.seconds || 0;
        const tB = b.createdAt?.seconds || 0;
        return tB - tA;
      });

      // ── 3. Time range filter ──
      const now = Date.now();
      let startTimeSeconds = 0;
      if (timeRange === 'today') startTimeSeconds = (now - 86400000) / 1000;
      else if (timeRange === 'week') startTimeSeconds = (now - 604800000) / 1000;
      else if (timeRange === 'month') startTimeSeconds = (now - 2592000000) / 1000;

      const filteredArtists = startTimeSeconds > 0
        ? enrichedArtists.filter(a => (a.createdAt?.seconds || 0) >= startTimeSeconds)
        : enrichedArtists;

      // ── 4. Fetch gigs ──
      const liveGigsSnap = await getDocs(collection(db, 'liveGigs'));

      let recentGigs = [];
      try {
        const recentGigsSnap = await getDocs(
          query(collection(db, 'liveGigs'), orderBy('actualStartTime', 'desc'), limit(10))
        );
        recentGigs = recentGigsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      } catch (e) {
        // fallback if index missing — just take the first 10
        recentGigs = liveGigsSnap.docs.slice(0, 10).map(d => ({ id: d.id, ...d.data() }));
      }

      let totalVotes = 0, totalSongRequests = 0;
      const totalAudienceMembers = new Set();
      liveGigsSnap.docs.forEach(d => {
        const g = d.data();
        if (g.votes) totalVotes += Object.values(g.votes).reduce((s, v) => s + (Number(v) || 0), 0);
        if (g.songRequests) totalSongRequests += g.songRequests.length;
        if (g.audienceTracking?.joinedUsers) {
          Object.keys(g.audienceTracking.joinedUsers).forEach(uid => totalAudienceMembers.add(uid));
        }
      });

      setStats({
        totalArtists: filteredArtists.length,
        totalUsers,
        totalGigs: liveGigsSnap.size,
        liveGigs: liveGigsSnap.docs.filter(d => d.data().status === 'live').length,
        completedGigs: liveGigsSnap.docs.filter(d => d.data().status === 'ended').length,
        totalAudience: totalAudienceMembers.size,
        totalVotes,
        totalSongRequests,
        recentSignups: sortedArtists.slice(0, 10),
        recentGigs,
        loading: false,
      });
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setStats(prev => ({ ...prev, loading: false }));
    }
  };

  // ════════════════════════════════════════
  // SONG BANK
  // ════════════════════════════════════════
  const loadTracks = async () => {
    setBankLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'backingTracks'), orderBy('title')));
      setTracks(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); }
    setBankLoading(false);
  };

  const loadPendingSongs = async () => {
    setPendingLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'backingTracks'), where('youtubeId', '==', null)));
      const snap2 = await getDocs(query(collection(db, 'backingTracks'), where('youtubeId', '==', '')));
      const all = [
        ...snap.docs.map(d => ({ id: d.id, ...d.data() })),
        ...snap2.docs.map(d => ({ id: d.id, ...d.data() })),
      ];
      const seen = new Set();
      setPendingSongs(all.filter(t => { if (seen.has(t.id)) return false; seen.add(t.id); return true; }));
    } catch (e) { console.error(e); }
    setPendingLoading(false);
  };

  const handleAddTrack = async () => {
    setFormError('');
    if (!form.title.trim()) { setFormError('Song title required.'); return; }
    if (!form.artist.trim()) { setFormError('Artist name required.'); return; }
    if (!form.youtubeUrl.trim()) { setFormError('YouTube URL required.'); return; }
    const ytId = extractYouTubeId(form.youtubeUrl.trim());
    if (!ytId) { setFormError('Invalid YouTube URL.'); return; }
    setSaving(true);
    try {
      const existing = tracks.find(
        t => t.title.toLowerCase() === form.title.trim().toLowerCase() &&
             t.artist.toLowerCase() === form.artist.trim().toLowerCase()
      );
      if (existing) {
        await updateDoc(doc(db, 'backingTracks', existing.id), {
          youtubeId: ytId, genre: form.genre, updatedAt: serverTimestamp(),
        });
      } else {
        await addDoc(collection(db, 'backingTracks'), {
          title: form.title.trim(), artist: form.artist.trim(), genre: form.genre,
          youtubeId: ytId, addedBy: currentUser.email, addedAt: serverTimestamp(),
        });
      }
      setForm({ title: '', artist: '', genre: 'Rock', youtubeUrl: '' });
      await loadTracks();
      await loadPendingSongs();
    } catch (e) { alert('Error: ' + e.message); }
    setSaving(false);
  };

  const handleLinkPending = async (track, youtubeUrl) => {
    const ytId = extractYouTubeId(youtubeUrl);
    if (!ytId) { alert('Invalid YouTube URL'); return; }
    try {
      await updateDoc(doc(db, 'backingTracks', track.id), { youtubeId: ytId, updatedAt: serverTimestamp() });
      await loadTracks();
      await loadPendingSongs();
    } catch (e) { alert('Error: ' + e.message); }
  };

  const handleDeleteTrack = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try {
      await deleteDoc(doc(db, 'backingTracks', id));
      setTracks(prev => prev.filter(t => t.id !== id));
    } catch (e) { alert('Error: ' + e.message); }
  };

  const filteredTracks = tracks.filter(t => {
    const s = bankSearch.toLowerCase();
    return !s || t.title.toLowerCase().includes(s) || t.artist.toLowerCase().includes(s) || (t.genre || '').toLowerCase().includes(s);
  });

  // ════════════════════════════════════════
  // GUARD
  // ════════════════════════════════════════
  if (currentUser?.email !== ADMIN_EMAIL) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl text-red-500 mb-4">🚫 Access Denied</h1>
          <p className="text-gray-400">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════
  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-purple-900/20 to-black">
      <div className="max-w-7xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-4xl font-bold text-neon mb-2" style={{ textShadow: '0 0 20px #39FF14' }}>
              🎸 GigWave Admin
            </h1>
            <p className="text-gray-400">Platform management</p>
          </div>
          <button onClick={onClose}
            className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-bold transition-all">
            ✕ Close
          </button>
        </div>

        {/* Tab navigation */}
        <div className="flex gap-2 mb-6">
          {[
            ['analytics', '📊 Analytics'],
            ['songbank', `🎸 Song Bank${pendingSongs.length > 0 ? ` (${pendingSongs.length} pending)` : ''}`],
            ['followers', `👥 Followers${followersArtists.filter(a => a.followerCountsBlocked).length > 0 ? ` (${followersArtists.filter(a => a.followerCountsBlocked).length} blocked)` : ''}`],
          ].map(([id, label]) => (
            <button key={id} onClick={() => setAdminTab(id)}
              className={`px-5 py-2.5 rounded-lg font-bold text-sm transition-all ${
                adminTab === id ? 'bg-neon text-black' : 'bg-white/10 text-gray-400 hover:bg-white/20'
              }`}
              style={adminTab === id ? { boxShadow: '0 0 15px rgba(57,255,20,0.5)' } : {}}>
              {label}
            </button>
          ))}
        </div>

        {/* ══ ANALYTICS TAB ══ */}
        {adminTab === 'analytics' && (
          <>
            {stats.loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-neon mx-auto mb-4"></div>
                  <p className="text-neon text-xl">Loading Analytics...</p>
                </div>
              </div>
            ) : (
              <>
                {/* Visitor Counter */}
                <div className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border-2 border-cyan-400 rounded-xl p-6 mb-6">
                  <div className="flex items-center gap-6">
                    <div className="text-6xl">📊</div>
                    <div className="flex-1">
                      <h2 className="text-xl font-bold text-gray-300 mb-1">Website Visitors</h2>
                      <div className="flex items-baseline gap-3">
                        <div className="text-5xl font-bold text-cyan-400">{visitorCount?.toLocaleString() || '0'}</div>
                        <div className="text-gray-400 text-sm">all-time visits</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Time Range Filter */}
                <div className="flex gap-2 mb-6">
                  {['today', 'week', 'month', 'all'].map(range => (
                    <button key={range} onClick={() => setTimeRange(range)}
                      className={`px-4 py-2 rounded-lg font-bold text-sm transition-all ${
                        timeRange === range ? 'bg-neon text-black' : 'bg-white/10 text-gray-400 hover:bg-white/20'
                      }`}>
                      {range === 'all' ? 'All Time' : range.charAt(0).toUpperCase() + range.slice(1)}
                    </button>
                  ))}
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <div className="bg-gradient-to-br from-purple-500/20 to-purple-700/20 border border-purple-400/30 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4"><Users className="text-purple-400" size={32} /><span className="text-3xl">🎤</span></div>
                    <h3 className="text-gray-400 text-sm mb-1">Artists (with profile)</h3>
                    <p className="text-4xl font-bold text-white">{stats.totalArtists}</p>
                    {timeRange !== 'all' && (
                      <p className="text-xs text-purple-300 mt-1">in selected period</p>
                    )}
                  </div>
                  <div className="bg-gradient-to-br from-indigo-500/20 to-indigo-700/20 border border-indigo-400/30 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4"><Users className="text-indigo-400" size={32} /><span className="text-3xl">👤</span></div>
                    <h3 className="text-gray-400 text-sm mb-1">Total Registered Users</h3>
                    <p className="text-4xl font-bold text-white">{stats.totalUsers}</p>
                    <p className="text-xs text-indigo-300 mt-1">all accounts (artists + audience)</p>
                  </div>
                  <div className="bg-gradient-to-br from-cyan-500/20 to-cyan-700/20 border border-cyan-400/30 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4"><Calendar className="text-cyan-400" size={32} /><span className="text-3xl">🎸</span></div>
                    <h3 className="text-gray-400 text-sm mb-1">Total Gigs</h3>
                    <p className="text-4xl font-bold text-white">{stats.totalGigs}</p>
                    <p className="text-xs text-cyan-300 mt-1">{stats.liveGigs} live · {stats.completedGigs} ended</p>
                  </div>
                  <div className="bg-gradient-to-br from-pink-500/20 to-pink-700/20 border border-pink-400/30 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4"><Activity className="text-pink-400" size={32} /><span className="text-3xl">👥</span></div>
                    <h3 className="text-gray-400 text-sm mb-1">Unique Audience</h3>
                    <p className="text-4xl font-bold text-white">{stats.totalAudience}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mb-8">
                  <div className="bg-gradient-to-br from-green-500/20 to-green-700/20 border border-green-400/30 rounded-xl p-4 flex items-center gap-6">
                    <div className="flex items-center gap-3"><TrendingUp className="text-green-400" size={28} /><span className="text-2xl">⚡</span></div>
                    <div>
                      <h3 className="text-gray-400 text-sm mb-1">Total Votes</h3>
                      <p className="text-3xl font-bold text-white">{stats.totalVotes}</p>
                    </div>
                    <div className="ml-8 text-xs text-gray-500 leading-relaxed">
                      <p>💡 <span className="text-gray-300">Artists (with profile)</span> = users who completed their artist profile and appear in venue search.</p>
                      <p className="mt-1">💡 <span className="text-gray-300">Total Registered Users</span> = everyone who signed up (artists + audience members).</p>
                      <p className="mt-1">⚠️ Run <span className="text-blue-400">Sync All Artist Profiles</span> below if artist count seems low.</p>
                    </div>
                  </div>
                </div>

                {/* Secondary Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-2"><Music className="text-cyan-400" size={24} /><h3 className="text-white font-bold">Song Requests</h3></div>
                    <p className="text-3xl font-bold text-cyan-400">{stats.totalSongRequests}</p>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-2"><Clock className="text-purple-400" size={24} /><h3 className="text-white font-bold">Avg Votes / Gig</h3></div>
                    <p className="text-3xl font-bold text-purple-400">
                      {stats.totalGigs > 0 ? Math.round(stats.totalVotes / stats.totalGigs) : 0}
                    </p>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-2"><BarChart3 className="text-green-400" size={24} /><h3 className="text-white font-bold">Requests / Gig</h3></div>
                    <p className="text-3xl font-bold text-green-400">
                      {stats.totalGigs > 0 ? Math.round(stats.totalSongRequests / stats.totalGigs) : 0}
                    </p>
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                  {/* Recent Artist Signups */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                    <h2 className="text-2xl font-bold text-electric mb-4">🎤 Recent Artist Sign-ups</h2>
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {stats.recentSignups.length === 0 ? (
                        <p className="text-gray-400 text-center py-4">No artists found</p>
                      ) : (
                        stats.recentSignups.map(artist => {
                          const ts = artist.createdAt?.seconds
                            ? new Date(artist.createdAt.seconds * 1000).toLocaleDateString()
                            : artist.createdAt?.toDate?.()
                              ? artist.createdAt.toDate().toLocaleDateString()
                              : 'N/A';
                          return (
                            <div key={artist.id} className="bg-white/5 border border-white/10 rounded-lg p-3">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-white font-bold">{artist.artistName}</p>
                                  <p className="text-gray-400 text-xs">{artist.email || artist.id}</p>
                                  {artist.genre && <p className="text-electric text-xs">🎵 {artist.genre}</p>}
                                  {artist.location && <p className="text-gray-500 text-xs">📍 {artist.location}</p>}
                                </div>
                                <p className="text-neon text-xs text-right">{ts}</p>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  {/* Recent Gigs */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                    <h2 className="text-2xl font-bold text-magenta mb-4">🎸 Recent Gigs</h2>
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {stats.recentGigs.length === 0 ? (
                        <p className="text-gray-400 text-center py-4">No recent gigs</p>
                      ) : (
                        stats.recentGigs.map(gig => (
                          <div key={gig.id} className="bg-white/5 border border-white/10 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex-1">
                                <p className="text-white font-bold">{gig.artistName}</p>
                                <p className="text-gray-400 text-xs">{gig.venueName || 'Unknown Venue'}</p>
                                {gig.gigDate && <p className="text-electric text-xs">📅 {gig.gigDate}</p>}
                              </div>
                              <div className={`px-2 py-1 rounded text-xs font-bold ${
                                gig.status === 'live' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                              }`}>
                                {gig.status === 'live' ? '🟢 LIVE' : gig.status === 'ended' ? '⚫ ENDED' : '🔵 ' + (gig.status || 'UPCOMING').toUpperCase()}
                              </div>
                            </div>
                            <div className="flex items-center gap-4 text-xs text-gray-400">
                              <span>👥 {gig.audienceTracking?.currentlyActive || 0}</span>
                              <span>⚡ {Object.values(gig.votes || {}).reduce((s, v) => s + v, 0)}</span>
                              <span>🎵 {(gig.songRequests || []).length}</span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row gap-4 items-center justify-center">
                  <button onClick={fetchAnalytics}
                    className="px-6 py-3 bg-neon hover:bg-neon/80 text-black font-bold rounded-lg transition-all">
                    🔄 Refresh Data
                  </button>
                  <div className="flex flex-col items-center gap-1">
                    <button
                      onClick={handleBackfillArtists}
                      disabled={backfilling}
                      className="px-6 py-3 font-bold rounded-lg transition-all"
                      style={{
                        background: backfilling ? '#333' : 'linear-gradient(135deg, #1e3a5f, #1d4ed8)',
                        color: 'white',
                        border: '1px solid rgba(59,130,246,0.5)',
                        opacity: backfilling ? 0.7 : 1,
                      }}>
                      {backfilling ? '⏳ Syncing...' : '🔄 Sync All Artist Profiles to Search Index'}
                    </button>
                    <p className="text-gray-500 text-xs">
                      Run this once to make all existing artists visible in the venue portal.
                      {backfillResult && ` Last run: ${backfillResult.synced} synced, ${backfillResult.skipped} skipped.`}
                    </p>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* ══ SONG BANK TAB ══ */}
        {adminTab === 'songbank' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            <div className="space-y-4">
              {/* Add New Track */}
              <div className="rounded-xl p-4"
                style={{ background: 'rgba(0,245,255,0.05)', border: '1px solid rgba(0,245,255,0.2)' }}>
                <div className="text-cyan-400 font-black text-sm mb-3">+ Add New Track</div>
                <div className="space-y-2 mb-2">
                  <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                    placeholder="Song title *"
                    className="w-full rounded-lg px-3 py-2 text-sm text-white"
                    style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', outline: 'none' }} />
                  <input value={form.artist} onChange={e => setForm(f => ({ ...f, artist: e.target.value }))}
                    placeholder="Original artist *"
                    className="w-full rounded-lg px-3 py-2 text-sm text-white"
                    style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', outline: 'none' }} />
                  <select value={form.genre} onChange={e => setForm(f => ({ ...f, genre: e.target.value }))}
                    className="w-full rounded-lg px-3 py-2 text-sm text-white"
                    style={{ background: 'rgba(20,5,40,0.95)', border: '1px solid rgba(255,255,255,0.15)', outline: 'none' }}>
                    {GENRES.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                  <input value={form.youtubeUrl} onChange={e => setForm(f => ({ ...f, youtubeUrl: e.target.value }))}
                    placeholder="YouTube backing track URL *"
                    className="w-full rounded-lg px-3 py-2 text-sm text-white"
                    style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', outline: 'none' }} />
                </div>
                {extractYouTubeId(form.youtubeUrl) && (
                  <div className="rounded-lg overflow-hidden mb-2" style={{ aspectRatio: '16/9' }}>
                    <iframe width="100%" height="100%"
                      src={`https://www.youtube.com/embed/${extractYouTubeId(form.youtubeUrl)}`}
                      allow="encrypted-media" allowFullScreen style={{ border: 'none', display: 'block' }} />
                  </div>
                )}
                {formError && <p className="text-red-400 text-xs mb-2">{formError}</p>}
                <button onClick={handleAddTrack} disabled={saving}
                  className="w-full py-2.5 rounded-xl text-sm font-black text-black transition-all"
                  style={{ background: saving ? '#333' : '#00f5ff', opacity: saving ? 0.7 : 1 }}>
                  {saving ? 'Saving...' : '+ Add to Song Bank'}
                </button>
              </div>

              {/* Pending Songs */}
              {(pendingSongs.length > 0 || pendingLoading) && (
                <div className="rounded-xl p-4"
                  style={{ background: 'rgba(255,200,0,0.05)', border: '1px solid rgba(255,200,0,0.25)' }}>
                  <div className="text-yellow-400 font-black text-sm mb-1">
                    ⏳ Pending Songs ({pendingSongs.length})
                  </div>
                  <p className="text-gray-500 text-xs mb-3">
                    Artists added these songs — they need a YouTube backing track link.
                  </p>
                  {pendingLoading ? (
                    <p className="text-gray-400 text-xs text-center py-2">Loading...</p>
                  ) : (
                    <div className="space-y-2">
                      {pendingSongs.map(track => (
                        <PendingTrackRow key={track.id} track={track} onLink={handleLinkPending} onDelete={handleDeleteTrack} />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Existing tracks */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-white font-bold">{filteredTracks.filter(t => t.youtubeId).length} tracks with links</span>
                <button onClick={loadTracks} className="text-xs text-cyan-400 hover:text-cyan-300">🔄 Refresh</button>
              </div>
              <input value={bankSearch} onChange={e => setBankSearch(e.target.value)}
                placeholder="Search tracks..."
                className="w-full rounded-lg px-3 py-2 text-sm text-white mb-3"
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', outline: 'none' }} />
              {bankLoading ? (
                <p className="text-gray-400 text-center py-8">Loading...</p>
              ) : (
                <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
                  {filteredTracks.filter(t => t.youtubeId).map(track => (
                    <div key={track.id} className="rounded-lg p-3"
                      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-white text-sm font-semibold truncate">{track.title}</div>
                          <div className="text-gray-400 text-xs truncate">
                            {track.artist}
                            {track.genre && <span className="ml-2 text-cyan-700">#{track.genre}</span>}
                            {track.addedByArtist && <span className="ml-2 text-yellow-600">artist</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button onClick={() => setPreviewId(previewId === track.youtubeId ? null : track.youtubeId)}
                            className="text-xs px-2 py-1 rounded font-bold"
                            style={{ background: 'rgba(0,245,255,0.12)', color: '#00f5ff', border: '1px solid rgba(0,245,255,0.25)' }}>
                            {previewId === track.youtubeId ? '⏹' : '▶'}
                          </button>
                          <button onClick={() => handleDeleteTrack(track.id, track.title)}
                            className="text-xs px-2 py-1 rounded font-bold"
                            style={{ background: 'rgba(255,50,50,0.12)', color: '#ff6b6b', border: '1px solid rgba(255,50,50,0.25)' }}>
                            🗑
                          </button>
                        </div>
                      </div>
                      {previewId === track.youtubeId && (
                        <div className="mt-2 rounded-lg overflow-hidden" style={{ aspectRatio: '16/9' }}>
                          <iframe width="100%" height="100%"
                            src={`https://www.youtube.com/embed/${track.youtubeId}?autoplay=1`}
                            allow="autoplay; encrypted-media" allowFullScreen style={{ border: 'none', display: 'block' }} />
                        </div>
                      )}
                    </div>
                  ))}
                  {filteredTracks.filter(t => t.youtubeId).length === 0 && (
                    <p className="text-gray-500 text-center py-8 text-sm">No tracks yet. Add the first one!</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══ FOLLOWERS TAB ══ */}
        {adminTab === 'followers' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold text-white">👥 Artist Follower Counts</h2>
                <p className="text-gray-400 text-sm mt-1">
                  Only shows artists who have <span className="text-white font-semibold">entered their follower counts</span>.
                  Artists who haven't filled this in yet won't appear here — that's expected.
                  Block any artist who appears to have inflated numbers; they'll see a banner to contact support.
                </p>
              </div>
              <button onClick={loadFollowersData}
                className="btn btn-ghost text-sm">🔄 Refresh</button>
            </div>

            {/* Search */}
            <input
              value={followersSearch}
              onChange={e => setFollowersSearch(e.target.value)}
              placeholder="Search by artist name..."
              className="w-full px-4 py-3 rounded-lg text-white mb-6"
              style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)', outline: 'none' }}
            />

            {followersLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-neon mx-auto mb-3"></div>
                <p className="text-neon">Loading follower data...</p>
              </div>
            ) : followersArtists.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-400 text-lg">No artists have entered follower counts yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {followersArtists
                  .filter(a => !followersSearch || a.artistName.toLowerCase().includes(followersSearch.toLowerCase()))
                  .map(artist => {
                    const updatedAt = artist.followerCountsUpdatedAt?.seconds
                      ? new Date(artist.followerCountsUpdatedAt.seconds * 1000).toLocaleDateString()
                      : 'Unknown';
                    const platforms = [
                      { key: 'instagram', icon: '📸', label: 'Instagram', color: '#E1306C' },
                      { key: 'youtube', icon: '▶️', label: 'YouTube', color: '#FF0000' },
                      { key: 'spotify', icon: '🎵', label: 'Spotify', color: '#1DB954' },
                      { key: 'facebook', icon: '👍', label: 'Facebook', color: '#1877F2' },
                      { key: 'tiktok', icon: '🎵', label: 'TikTok', color: '#ee1d52' },
                      { key: 'twitter', icon: '🐦', label: 'Twitter', color: '#1DA1F2' },
                      { key: 'soundcloud', icon: '☁️', label: 'SoundCloud', color: '#FF5500' },
                    ];
                    return (
                      <div key={artist.id} className="rounded-xl p-5"
                        style={{
                          background: artist.followerCountsBlocked
                            ? 'rgba(239,68,68,0.06)'
                            : 'rgba(255,255,255,0.04)',
                          border: artist.followerCountsBlocked
                            ? '1px solid rgba(239,68,68,0.4)'
                            : '1px solid rgba(255,255,255,0.1)',
                        }}>

                        {/* Artist header */}
                        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4">
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="text-white font-bold text-lg">{artist.artistName}</h3>
                              {artist.followerCountsBlocked && (
                                <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                                  style={{ background: 'rgba(239,68,68,0.2)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.5)' }}>
                                  🚫 BLOCKED
                                </span>
                              )}
                            </div>
                            <p className="text-gray-400 text-sm">{artist.email}</p>
                            {artist.location && <p className="text-gray-500 text-xs">📍 {artist.location}</p>}
                            <p className="text-gray-500 text-xs mt-1">Last updated: {updatedAt}</p>
                          </div>

                          <div className="flex gap-2 flex-shrink-0">
                            {/* Verify — visit their social links */}
                            {artist.socialMedia && Object.values(artist.socialMedia).some(v => v) && (
                              <div className="flex gap-1">
                                {[
                                  { key: 'instagram', icon: '📸', base: 'https://instagram.com/' },
                                  { key: 'youtube', icon: '▶️', base: 'https://youtube.com/' },
                                  { key: 'tiktok', icon: '🎵', base: 'https://tiktok.com/@' },
                                ].map(({ key, icon, base }) => {
                                  const val = artist.socialMedia[key];
                                  if (!val) return null;
                                  const url = val.startsWith('http') ? val : `${base}${val.replace('@', '')}`;
                                  return (
                                    <a key={key} href={url} target="_blank" rel="noopener noreferrer"
                                      title={`Verify ${key}`}
                                      className="text-sm px-2 py-1 rounded font-bold"
                                      style={{ background: 'rgba(255,255,255,0.08)', color: '#9ca3af', border: '1px solid rgba(255,255,255,0.15)' }}>
                                      {icon}
                                    </a>
                                  );
                                })}
                              </div>
                            )}

                            {/* Block / Unblock */}
                            {artist.followerCountsBlocked ? (
                              <button
                                onClick={() => handleUnblockFollowers(artist.id, artist.artistName)}
                                disabled={blockingId === artist.id}
                                className="text-sm px-3 py-1.5 rounded-lg font-bold transition-all"
                                style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.4)' }}>
                                {blockingId === artist.id ? '⏳' : '✅ Reinstate'}
                              </button>
                            ) : (
                              <button
                                onClick={() => handleBlockFollowers(artist.id, artist.artistName)}
                                disabled={blockingId === artist.id}
                                className="text-sm px-3 py-1.5 rounded-lg font-bold transition-all"
                                style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.4)' }}>
                                {blockingId === artist.id ? '⏳' : '🚫 Block'}
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Follower counts grid */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {platforms.map(({ key, icon, label, color }) => {
                            const val = artist.followerCounts?.[key];
                            if (!val) return null;
                            return (
                              <div key={key} className="flex items-center gap-2 p-2 rounded-lg"
                                style={{ background: `${color}10`, border: `1px solid ${color}25` }}>
                                <span className="text-base">{icon}</span>
                                <div>
                                  <p className="text-white font-bold text-sm leading-tight">
                                    {Number(val).toLocaleString()}
                                  </p>
                                  <p className="text-xs" style={{ color }}>{label}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

function PendingTrackRow({ track, onLink, onDelete }) {
  const [url, setUrl] = useState('');
  return (
    <div className="rounded-lg p-2"
      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,200,0,0.15)' }}>
      <div className="text-white text-sm font-semibold truncate">{track.title}</div>
      <div className="text-gray-400 text-xs mb-2">{track.artist}</div>
      <div className="flex gap-2">
        <input value={url} onChange={e => setUrl(e.target.value)}
          placeholder="Paste YouTube URL..."
          className="flex-1 rounded px-2 py-1 text-xs text-white"
          style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,200,0,0.2)', outline: 'none' }} />
        <button onClick={() => onLink(track, url)}
          className="text-xs px-2 py-1 rounded font-bold"
          style={{ background: 'rgba(0,245,255,0.15)', color: '#00f5ff', border: '1px solid rgba(0,245,255,0.3)' }}>
          Link
        </button>
        <button onClick={() => onDelete(track.id, track.title)}
          className="text-xs px-2 py-1 rounded font-bold"
          style={{ background: 'rgba(255,50,50,0.1)', color: '#ff6b6b', border: '1px solid rgba(255,50,50,0.2)' }}>
          🗑
        </button>
      </div>
    </div>
  );
}

export default AdminDashboard;
